import React, { useRef, useImperativeHandle, forwardRef } from "react";
import { Turnstile as CFTurnstile } from "@marsidev/react-turnstile";

/**
 * Brand-styled Cloudflare Turnstile widget.
 *
 * Usage:
 *   const ref = useRef();
 *   <Turnstile ref={ref} onToken={setToken} />
 *   ref.current.reset();   // call after a successful submit
 *
 * If REACT_APP_TURNSTILE_SITE_KEY is unset, this component renders nothing
 * and parents should treat the token as optional (matches backend skip behaviour).
 */
const Turnstile = forwardRef(({ onToken, onError, action }, ref) => {
  const innerRef = useRef(null);
  const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;

  useImperativeHandle(ref, () => ({
    reset: () => innerRef.current?.reset(),
    execute: () => innerRef.current?.execute(),
    getResponse: () => innerRef.current?.getResponse(),
  }), []);

  if (!siteKey) return null;

  return (
    <div data-testid="turnstile-widget" className="flex justify-start">
      <CFTurnstile
        ref={innerRef}
        siteKey={siteKey}
        options={{
          theme: "dark",
          appearance: "always",
          size: "flexible",
          action: action || "submit",
        }}
        onSuccess={(token) => onToken && onToken(token)}
        onExpire={() => onToken && onToken("")}
        onError={(err) => onError && onError(err)}
      />
    </div>
  );
});

Turnstile.displayName = "Turnstile";
export default Turnstile;
